<template>
    <div class="after-sales-mobile">
        <van-nav-bar title="售后接件" left-arrow @click-left="$router.back()">
            <template #right>
                <van-tag round type="primary" class="workbench-link" @click="$router.push({ name: 'internal-workbench' })">内部工作台</van-tag>
            </template>
        </van-nav-bar>

        <main class="mobile-content">
            <section class="hero-card">
                <div class="hero-copy">
                    <span class="eyebrow">AFTER-SALES INTAKE</span>
                    <h1>扫码开始接件</h1>
                    <p>先确认设备身份，再记录问题和现场照片</p>
                </div>
                <div class="hero-icon" aria-hidden="true"><van-icon name="scan" /></div>
            </section>

            <section class="card scan-card">
                <div class="section-title">
                    <div><span class="step">01</span><strong>识别设备</strong></div>
                    <van-tag v-if="currentDevice" type="success">已匹配</van-tag>
                </div>
                <DeviceScanner ref="deviceScanner" @scanned="handleScanned" @error="handleScannerError" />
                <div class="no-code-panel">
                    <div class="no-code-copy">
                        <span class="no-code-kicker">无原厂 SN</span>
                        <strong>早期设备也能快速建档</strong>
                        <span>生成内部 SN 后打印标签并贴到设备上</span>
                    </div>
                    <van-button class="no-code-button" type="default" icon="plus" :loading="resolving" :disabled="!!currentDevice" @click="registerDevice(true)">生成内部 SN</van-button>
                </div>
                <van-notice-bar v-if="scanError" mode="closeable" left-icon="warning-o" :text="scanError" @close="scanError = ''" />
            </section>

            <section v-if="notFound" class="card not-found-card">
                <div class="not-found-icon"><van-icon name="question-o" /></div>
                <div class="not-found-copy">
                    <strong>未找到设备档案</strong>
                    <span>当前编码：{{ scanValue || '未录入' }}</span>
                </div>
                <p>早期设备没有原厂 SN 时，可以在这里生成内部 SN 并打印标签。</p>
                <div class="not-found-actions">
                    <van-button plain type="primary" size="small" @click="registerDevice(false)">以当前编号建档</van-button>
                    <van-button type="primary" size="small" @click="registerDevice(true)">无 SN，生成内部 SN</van-button>
                </div>
            </section>

            <section v-if="currentDevice" class="card device-card">
                <div class="section-title">
                    <div><span class="step">02</span><strong>设备确认</strong></div>
                    <van-tag :type="currentDevice.sn_type === 'generated' ? 'warning' : 'success'">
                        {{ currentDevice.sn_type === 'generated' ? '内部 SN' : '原厂 SN' }}
                    </van-tag>
                </div>
                <div class="device-main">
                    <div class="device-avatar"><van-icon name="desktop-o" /></div>
                    <div class="device-copy">
                        <strong>{{ currentDevice.product_name || currentDevice.model || '待补充设备型号' }}</strong>
                        <span class="device-sn">{{ currentDevice.internal_sn || currentDevice.device_sn }}</span>
                        <span v-if="currentDevice.original_sn">原厂 SN：{{ currentDevice.original_sn }}</span>
                        <span v-else>原厂 SN：无</span>
                    </div>
                </div>
                <div class="device-facts">
                    <span><small>状态</small>{{ deviceStatusText(currentDevice.status) }}</span>
                    <span><small>历史售后</small>{{ currentDevice.order_count || 0 }} 次</span>
                    <span><small>标签</small>{{ currentDevice.label_status === 'verified' ? '已复核' : '待复核' }}</span>
                </div>
                <van-button v-if="currentDevice.sn_type === 'generated' && !labelVerified" block plain type="warning" icon="scan" @click="startLabelVerify">扫描标签完成复核</van-button>
                <van-button block type="primary" :disabled="currentDevice.sn_type === 'generated' && !labelVerified" @click="beginIntake">进入接件录入</van-button>
            </section>

            <section v-if="intakeStarted" class="card intake-card">
                <div class="section-title">
                    <div><span class="step">03</span><strong>填写接件信息</strong></div>
                    <span class="draft-state">{{ draftSavedAt ? '草稿已保存' : '自动保存' }}</span>
                </div>
                <van-cell-group inset>
                    <van-field v-model.trim="draft.productModel" label="设备型号" placeholder="可手工补充" maxlength="80" />
                    <van-field v-model.trim="draft.contactName" label="联系人" placeholder="请输入联系人" maxlength="30" />
                    <van-field v-model.trim="draft.phone" label="联系方式" type="tel" placeholder="请输入手机号或座机" maxlength="30" />
                    <van-field v-model="draft.source" readonly clickable label="接件来源" placeholder="请选择" is-link @click="showSources = true" />
                    <van-field v-model="draft.issueType" readonly clickable label="问题类型" placeholder="请选择问题类型" is-link @click="showIssueTypes = true" />
                    <van-field v-model.trim="draft.description" label="问题描述" type="textarea" rows="4" autosize maxlength="500" show-word-limit placeholder="请描述故障现象、发生场景和复现步骤" />
                    <van-field v-model.trim="draft.appearance" label="外观情况" type="textarea" rows="2" autosize maxlength="200" placeholder="例如：右上角有磕碰，屏幕有划痕" />
                </van-cell-group>

                <div class="accessory-block">
                    <div class="field-label">随件物品</div>
                    <div class="chip-list">
                        <button v-for="item in accessoryOptions" :key="item" type="button" :class="{ active: draft.accessories.indexOf(item) !== -1 }" @click="toggleAccessory(item)">{{ item }}</button>
                    </div>
                </div>

                <div class="photo-block">
                    <div class="field-label">现场照片 <span>建议上传设备整体、标签和故障部位</span></div>
                    <van-uploader v-model="draft.files" :max-count="6" :max-size="5 * 1024 * 1024" multiple :before-read="beforeReadImage" @oversize="onImageOversize" />
                </div>

                <van-checkbox v-model="draft.privacyConfirmed" icon-size="18px" shape="square" class="privacy-check">已确认设备内无须保留的个人数据，或已取得数据处理授权</van-checkbox>
                <van-button class="submit-intake" block type="primary" :loading="submitting" :disabled="!canSubmit" @click="submitIntake">提交接件并创建售后单</van-button>
            </section>

            <section v-if="lastOrder" class="card success-card">
                <van-icon name="checked" />
                <div><strong>接件已提交</strong><span>{{ lastOrder.order_no || lastOrder.id || '售后单已创建' }}</span></div>
                <van-button type="primary" size="small" @click="openWorkflow">继续处理售后流程</van-button>
                <van-button plain type="primary" size="small" @click="resetForNext">继续接下一台</van-button>
            </section>
        </main>

        <van-popup v-model="labelVisible" round position="bottom" :style="{ minHeight: '58%' }">
            <div class="label-sheet">
                <div class="sheet-title"><strong>内部 SN 标签</strong><van-icon name="cross" @click="labelVisible = false" /></div>
                <p>请打印并粘贴标签，完成后用手机再次扫描确认设备身份。</p>
                <div class="print-label" id="after-sales-print-label">
                    <div class="print-label-title">内部设备标识</div>
                    <div class="print-label-code">{{ label.internal_sn }}</div>
                    <img v-if="label.qrDataUrl" :src="label.qrDataUrl" alt="内部SN二维码">
                    <small>{{ label.product_name || '售后设备' }}</small>
                </div>
                <div class="label-actions">
                    <van-button block plain type="primary" :loading="printing" @click="printLabel">打印 / 保存标签</van-button>
                    <van-button block type="primary" icon="scan" @click="startLabelVerify">我已贴好，扫描复核</van-button>
                </div>
            </div>
        </van-popup>

        <van-action-sheet v-model="showIssueTypes" :actions="issueTypeActions" cancel-text="取消" close-on-click-action @select="selectIssueType" />
        <van-action-sheet v-model="showSources" :actions="sourceActions" cancel-text="取消" close-on-click-action @select="selectSource" />
    </div>
</template>

<script>
import QRCode from 'qrcode'
import DeviceScanner from '@/components/DeviceScanner.vue'
import afterSalesApi from '@/api/after_sales_internal_api.js'

const EMPTY_DRAFT = () => ({
    productModel: '',
    contactName: '',
    phone: '',
    source: '客户寄送',
    issueType: '',
    description: '',
    appearance: '',
    accessories: [],
    files: [],
    privacyConfirmed: false
})

export default {
    name: 'AfterSalesMobile',
    components: { DeviceScanner },
    data() {
        return {
            scanValue: '',
            scanError: '',
            resolving: false,
            notFound: false,
            currentDevice: null,
            intakeStarted: false,
            draft: EMPTY_DRAFT(),
            draftSavedAt: '',
            submitting: false,
            lastOrder: null,
            labelVisible: false,
            labelVerified: false,
            label: { internal_sn: '', product_name: '', qrDataUrl: '' },
            printing: false,
            verifyingLabel: false,
            showIssueTypes: false,
            showSources: false,
            issueTypeActions: [
                { name: '无法开机', value: '无法开机' },
                { name: '功能异常', value: '功能异常' },
                { name: '外观损坏', value: '外观损坏' },
                { name: '网络/连接问题', value: '网络/连接问题' },
                { name: '软件/配置问题', value: '软件/配置问题' },
                { name: '其他', value: '其他' }
            ],
            sourceActions: [
                { name: '客户寄送', value: '客户寄送' },
                { name: '现场接件', value: '现场接件' },
                { name: '内部转交', value: '内部转交' },
                { name: '仓库发现', value: '仓库发现' }
            ],
            accessoryOptions: ['电源适配器', '电源线', '数据线', '遥控器', '说明书', '包装盒', '其他']
        }
    },
    computed: {
        appId() {
            return this.$root.app_id || ''
        },
        canSubmit() {
            return !!(this.currentDevice && this.draft.issueType && this.draft.description && this.draft.privacyConfirmed && !this.submitting)
        },
        draftKey() {
            const sn = this.currentDevice && (this.currentDevice.internal_sn || this.currentDevice.device_sn)
            return sn ? `after_sales_intake_draft_${sn}` : ''
        }
    },
    watch: {
        draft: {
            deep: true,
            handler() { this.saveDraft() }
        }
    },
    mounted() {
        document.title = '售后接件'
        const queryCode = this.$route.query.device_sn || this.$route.query.internal_sn || this.$route.query.sn
        if (queryCode) this.handleScanned(queryCode)
    },
    methods: {
        async handleScanned(code) {
            this.scanValue = code
            if (this.verifyingLabel) {
                this.verifyLabel(code)
                return
            }
            await this.resolveDevice(code)
        },

        async resolveDevice(code) {
            if (!code || this.resolving) return
            this.resolving = true
            this.scanError = ''
            this.notFound = false
            try {
                const res = await afterSalesApi.resolveDevice(this.appId, code)
                const payload = res && (res.other || res.data || res.result)
                if (res && res.errcode === 0 && payload) {
                    const device = this.normalizeDevice(payload, code, 'original')
                    if (this.redirectActiveOrder(device)) return
                    this.currentDevice = device
                    this.labelVerified = this.currentDevice.sn_type !== 'generated' || this.currentDevice.label_status === 'verified'
                    this.intakeStarted = false
                    this.lastOrder = null
                    this.loadDraft()
                } else if (this.isNotFound(res)) {
                    this.currentDevice = null
                    this.notFound = true
                } else {
                    throw new Error((res && (res.errmsg || res.msg)) || '设备查询失败')
                }
            } catch (error) {
                this.scanError = error.message || '设备查询失败，请检查网络后重试'
            } finally {
                this.resolving = false
            }
        },

        normalizeDevice(payload, fallbackCode, defaultSnType) {
            const item = payload || {}
            return Object.assign({}, item, {
                internal_sn: item.internal_sn || item.device_sn || fallbackCode,
                device_sn: item.device_sn || item.internal_sn || fallbackCode,
                sn_type: item.sn_type || (item.original_sn ? 'original' : (defaultSnType || 'original'))
            })
        },

        redirectActiveOrder(device) {
            const orderId = Number(device && device.active_order_id || 0)
            const active = device && (device.has_active_order === true || ['pending', 'processing'].indexOf(device.active_work_status) !== -1)
            if (!active || !orderId) return false
            this.$toast('该设备已有售后工单，正在打开')
            this.$router.replace({ name: 'after-sales-workflow', query: { order_id: orderId } })
            return true
        },

        isNotFound(res) {
            if (!res) return false
            const result = res.result || res.other
            return res.status === 404 || res.errcode === 404 || res.not_found === true || result === 'not_found' || (result && result.result === 'not_found')
        },

        async registerDevice(generated) {
            if (this.resolving) return
            this.resolving = true
            this.scanError = ''
            try {
                const res = await afterSalesApi.registerDevice(this.appId, {
                    original_sn: generated ? '' : this.scanValue,
                    product_model: this.draft.productModel || '',
                    label_required: true,
                    source: 'mobile_intake'
                })
                const payload = res && (res.other || res.data || res.result)
                if (!res || res.errcode !== 0 || !payload) throw new Error((res && (res.errmsg || res.msg)) || '内部 SN 生成失败')
                const device = this.normalizeDevice(payload, payload.internal_sn)
                if (this.redirectActiveOrder(device)) return
                device.sn_type = device.sn_type || (generated ? 'generated' : 'original')
                device.label_status = device.label_status || (device.sn_type === 'generated' ? 'pending' : 'verified')
                this.currentDevice = device
                this.scanValue = device.internal_sn
                this.notFound = false
                this.labelVerified = device.sn_type !== 'generated' || device.label_status === 'verified'
                this.intakeStarted = false
                if (device.sn_type === 'generated') {
                    await this.prepareLabel(device)
                    this.labelVisible = true
                } else {
                    this.labelVisible = false
                }
            } catch (error) {
                this.scanError = error.message || '内部 SN 生成失败，请稍后重试'
            } finally {
                this.resolving = false
            }
        },

        async prepareLabel(device) {
            const internalSn = device.internal_sn || device.device_sn
            const qrDataUrl = await QRCode.toDataURL(internalSn, { width: 240, margin: 1, errorCorrectionLevel: 'M' })
            this.label = {
                internal_sn: internalSn,
                product_name: device.product_name || device.model || this.draft.productModel,
                qrDataUrl
            }
        },

        async printLabel() {
            if (!this.label.internal_sn) return
            this.printing = true
            try {
                const res = await afterSalesApi.printLabel(this.appId, {
                    device_id: this.currentDevice.device_id || this.currentDevice.id,
                    internal_sn: this.label.internal_sn,
                    copies: 1
                })
                if (res && res.errcode !== 0 && res.status && res.status !== 404) throw new Error(res.errmsg || '打印任务提交失败')
                this.openBrowserPrint()
                this.$toast.success(res && res.errcode === 0 ? '打印任务已提交' : '已打开标签打印预览')
            } catch (error) {
                this.$toast(error.message || '打印失败')
            } finally {
                this.printing = false
            }
        },

        openBrowserPrint() {
            const win = window.open('', '_blank')
            if (!win) {
                this.$toast('浏览器阻止了打印窗口，请允许弹窗后重试')
                return
            }
            const title = this.escapeHtml(this.label.product_name || '售后设备')
            const sn = this.escapeHtml(this.label.internal_sn)
            win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${sn}</title><style>body{margin:0;padding:20px;font-family:Arial,'Microsoft YaHei',sans-serif}.label{width:360px;min-height:230px;border:2px solid #111;padding:18px;box-sizing:border-box;text-align:center}.title{font-size:18px;font-weight:bold}.sn{font:700 20px 'Courier New',monospace;margin:12px 0;word-break:break-all}.label img{width:150px;height:150px;object-fit:contain}.name{font-size:14px;margin-top:8px}@media print{button{display:none}}</style></head><body><div class="label"><div class="title">内部设备标识</div><div class="sn">${sn}</div><img src="${this.label.qrDataUrl}" alt="二维码"><div class="name">${title}</div></div><script>window.onload=function(){setTimeout(function(){window.print()},200)}<\/script></body></html>`)
            win.document.close()
        },

        startLabelVerify() {
            this.labelVisible = false
            this.verifyingLabel = true
            this.$toast('请扫描刚贴好的内部 SN 标签')
            this.$nextTick(() => this.$refs.deviceScanner && this.$refs.deviceScanner.openScanner())
        },

        verifyLabel(code) {
            this.verifyingLabel = false
            const expected = String(this.label.internal_sn || this.currentDevice.internal_sn || '').trim().toUpperCase()
            if (String(code || '').trim().toUpperCase() === expected) {
                this.labelVerified = true
                if (this.currentDevice) this.currentDevice.label_status = 'verified'
                this.$toast.success('标签复核通过')
            } else {
                this.$toast.fail('扫描结果与当前设备不一致')
                this.labelVisible = true
            }
        },

        beginIntake() {
            this.intakeStarted = true
            this.loadDraft()
            this.$nextTick(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }))
        },

        selectIssueType(action) { this.draft.issueType = action.value || action.name },
        selectSource(action) { this.draft.source = action.value || action.name },

        toggleAccessory(item) {
            const index = this.draft.accessories.indexOf(item)
            if (index === -1) this.draft.accessories.push(item)
            else this.draft.accessories.splice(index, 1)
        },

        beforeReadImage(file) {
            const image = Array.isArray(file) ? file[0] : file
            if (image && image.size && image.size > 5 * 1024 * 1024) {
                this.$toast('单张照片不能超过 5MB')
                return false
            }
            return true
        },

        onImageOversize() { this.$toast('单张照片不能超过 5MB') },

        saveDraft() {
            if (!this.draftKey) return
            const draft = Object.assign({}, this.draft, { files: [] })
            localStorage.setItem(this.draftKey, JSON.stringify(draft))
            this.draftSavedAt = new Date().toLocaleTimeString()
        },

        loadDraft() {
            this.draft = EMPTY_DRAFT()
            this.draftSavedAt = ''
            if (!this.draftKey) return
            try {
                const saved = JSON.parse(localStorage.getItem(this.draftKey) || 'null')
                if (saved) this.draft = Object.assign(EMPTY_DRAFT(), saved, { files: [] })
            } catch (e) {
                localStorage.removeItem(this.draftKey)
            }
        },

        async submitIntake() {
            if (!this.canSubmit) return
            this.submitting = true
            try {
                const imageUrls = []
                for (const entry of this.draft.files || []) {
                    const file = entry && (entry.file || entry)
                    if (!file) continue
                    const upload = await afterSalesApi.uploadImage(this.appId, file, {
                        device_id: this.currentDevice.device_id || this.currentDevice.id,
                        internal_sn: this.currentDevice.internal_sn || this.currentDevice.device_sn
                    })
                    if (!upload || upload.errcode !== 0) throw new Error((upload && (upload.errmsg || upload.msg)) || '照片上传失败')
                    const payload = upload.other || upload.data || upload.result || {}
                    imageUrls.push(payload.url || payload.path || payload.file_url || payload)
                }
                const res = await afterSalesApi.createIntake(this.appId, {
                    device_id: this.currentDevice.device_id || this.currentDevice.id,
                    internal_sn: this.currentDevice.internal_sn || this.currentDevice.device_sn,
                    original_sn: this.currentDevice.original_sn || '',
                    product_model: this.draft.productModel,
                    contact_name: this.draft.contactName,
                    phone: this.draft.phone,
                    source: this.draft.source,
                    issue_type: this.draft.issueType,
                    description: this.draft.description,
                    appearance: this.draft.appearance,
                    accessories: JSON.stringify(this.draft.accessories),
                    images: JSON.stringify(imageUrls),
                    label_verified: this.labelVerified ? 1 : 0,
                    privacy_confirmed: this.draft.privacyConfirmed ? 1 : 0
                })
                const payload = res && (res.other || res.data || res.result)
                if (!res || res.errcode !== 0 || !payload) throw new Error((res && (res.errmsg || res.msg)) || '接件提交失败')
                this.lastOrder = payload
                if (this.draftKey) localStorage.removeItem(this.draftKey)
                this.$toast.success('接件已提交')
            } catch (error) {
                this.$toast.fail(error.message || '提交失败，请稍后重试')
            } finally {
                this.submitting = false
            }
        },

        resetForNext() {
            this.scanValue = ''
            this.scanError = ''
            this.notFound = false
            this.currentDevice = null
            this.intakeStarted = false
            this.draft = EMPTY_DRAFT()
            this.lastOrder = null
            this.labelVerified = false
            this.label = { internal_sn: '', product_name: '', qrDataUrl: '' }
            window.scrollTo({ top: 0, behavior: 'smooth' })
        },

        openWorkflow() {
            const orderId = this.lastOrder && (this.lastOrder.order_id || this.lastOrder.id)
            if (!orderId) return this.$toast('未找到售后工单ID')
            this.$router.push({ name: 'after-sales-workflow', query: { order_id: orderId } })
        },

        handleScannerError(error) {
            this.scanError = (error && error.message) || '摄像头启动失败，请检查权限或使用手工输入'
        },

        deviceStatusText(status) {
            const map = { received: '已接件', repairing: '维修中', shipped: '已寄送', closed: '已关闭' }
            return map[status] || '待处理'
        },

        escapeHtml(value) {
            return String(value || '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))
        }
    }
}
</script>

<style scoped>
.after-sales-mobile { min-height: 100vh; height: 100%; overflow-y: auto; background: #f5f8fb; color: #1f2937; padding-bottom: 36px; font-size: 20px; }
.mobile-content { max-width: 760px; margin: 0 auto; padding: 14px 16px 48px; }
.hero-card { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 24px 22px; border-radius: 18px; color: #fff; background: linear-gradient(135deg, #315fe8, #5b8dff); box-shadow: 0 12px 28px rgba(49,95,232,.2); }
.hero-copy { min-width: 0; }
.eyebrow { display: block; font-size: 16px; letter-spacing: 1.4px; opacity: .78; }
.hero-card h1 { margin: 7px 0 4px; font-size: 34px; line-height: 1.2; }
.hero-card p { max-width: 390px; margin: 0; font-size: 21px; line-height: 1.45; opacity: .88; }
.hero-icon { display: flex; flex: 0 0 82px; align-items: center; justify-content: center; width: 82px; height: 82px; border-radius: 24px; background: rgba(255,255,255,.16); font-size: 45px; }
.workbench-link { cursor: pointer; transition: background-color .2s ease, transform .2s ease; }
.workbench-link:active { transform: scale(.96); }
.card { margin-top: 16px; padding: 20px 16px; border: 1px solid #e9eef7; border-radius: 18px; background: #fff; box-shadow: 0 8px 22px rgba(31,41,55,.06); }
.section-title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.section-title > div { display: flex; align-items: center; gap: 10px; }
.section-title strong { font-size: 27px; }
.step { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 50%; background: #e8efff; color: #315fe8; font: 700 16px/1 Arial; }
.draft-state { color: #9aa5b5; font-size: 20px; }
.scan-card .van-notice-bar { margin-top: 14px; border-radius: 10px; }
.no-code-panel { display: flex; align-items: center; gap: 14px; margin-top: 16px; padding: 14px; border: 1px solid #dbe7ff; border-radius: 14px; background: #f8fbff; }
.no-code-copy { display: flex; flex: 1; min-width: 0; flex-direction: column; gap: 2px; }
.no-code-kicker { color: #315fe8; font-size: 16px; font-weight: 600; letter-spacing: .3px; }
.no-code-copy strong { color: #25324a; font-size: 20px; line-height: 1.35; }
.no-code-copy span:last-child { color: #8490a3; font-size: 17px; line-height: 1.4; }
.no-code-button { flex: 0 0 auto; min-width: 132px; height: 52px; margin-top: 0; padding: 0 12px; border: 1px solid #b8ccff !important; border-radius: 12px; color: #315fe8 !important; background: #fff !important; box-shadow: none !important; white-space: nowrap; }
.no-code-button ::v-deep .van-button__text { color: #315fe8 !important; }
.not-found-card { display: flex; flex-wrap: wrap; align-items: center; gap: 14px; border: 1px solid #ffe3c0; background: #fffaf2; }
.not-found-icon { display: flex; align-items: center; justify-content: center; width: 54px; height: 54px; border-radius: 16px; background: #fff0d5; color: #e89a35; font-size: 30px; }
.not-found-copy { display: flex; flex: 1; flex-direction: column; min-width: 180px; }
.not-found-copy strong { font-size: 25px; }
.not-found-copy span { margin-top: 3px; color: #9c8059; font: 19px/1.35 'Courier New', monospace; word-break: break-all; }
.not-found-card > p { width: 100%; color: #9a7d54; font-size: 21px; }
.not-found-actions { display: flex; width: 100%; gap: 10px; }
.not-found-actions .van-button { flex: 1; height: 74px; }
.device-main { display: flex; align-items: center; gap: 16px; }
.device-avatar { display: flex; align-items: center; justify-content: center; flex: 0 0 70px; height: 70px; border-radius: 18px; color: #315fe8; background: #eaf0ff; font-size: 34px; }
.device-copy { display: flex; flex-direction: column; min-width: 0; gap: 4px; }
.device-copy strong { font-size: 26px; }
.device-copy span { color: #8490a3; font-size: 20px; }
.device-copy .device-sn { color: #315fe8; font: 700 21px/1.35 'Courier New', monospace; word-break: break-all; }
.device-facts { display: grid; grid-template-columns: repeat(3, 1fr); margin: 20px 0; padding: 14px 0; border-top: 1px solid #edf0f5; border-bottom: 1px solid #edf0f5; }
.device-facts span { display: flex; flex-direction: column; align-items: center; color: #354052; font-size: 22px; border-right: 1px solid #edf0f5; }
.device-facts span:last-child { border-right: 0; }
.device-facts small { margin-bottom: 3px; color: #9aa5b5; font-size: 18px; }
.device-card .van-button + .van-button { margin-top: 12px; }
.intake-card .van-cell-group { margin: 0; }
.intake-card .van-cell { min-height: 68px; padding: 13px 0; }
.intake-card .van-cell::after { left: 0; right: 0; }
.intake-card .van-field__label { width: 104px; color: #667388; }
.accessory-block, .photo-block { padding-top: 18px; }
.field-label { margin-bottom: 12px; color: #667388; font-size: 22px; }
.field-label span { margin-left: 8px; color: #a3adba; font-size: 18px; }
.chip-list { display: flex; flex-wrap: wrap; gap: 10px; }
.chip-list button { padding: 10px 16px; border: 1px solid #dde4ee; border-radius: 999px; color: #68758a; background: #fff; font-size: 20px; }
.chip-list button.active { border-color: #4e89ff; color: #315fe8; background: #edf3ff; }
.privacy-check { margin-top: 24px; color: #718096; font-size: 19px; line-height: 1.4; }
.submit-intake { height: 82px; margin-top: 24px; border-radius: 14px; font-size: 26px; }
.success-card { display: flex; align-items: center; gap: 12px; border: 1px solid #ccefd2; background: #f5fff6; }
.success-card > .van-icon { color: #45b85a; font-size: 42px; }
.success-card > div { display: flex; flex: 1; flex-direction: column; }
.success-card strong { font-size: 24px; }
.success-card span { color: #77907c; font-size: 19px; }
.label-sheet { padding: 20px 20px 32px; }
.sheet-title { display: flex; align-items: center; justify-content: space-between; font-size: 28px; }
.sheet-title .van-icon { color: #9aa5b5; font-size: 25px; }
.label-sheet > p { margin: 12px 0 18px; color: #7c8798; font-size: 21px; }
.print-label { display: flex; flex-direction: column; align-items: center; width: 320px; max-width: 100%; margin: 0 auto 20px; padding: 18px; border: 2px solid #111; color: #111; background: #fff; text-align: center; }
.print-label-title { font-size: 20px; font-weight: 700; }
.print-label-code { margin: 10px 0; font: 700 21px/1.3 'Courier New', monospace; word-break: break-all; }
.print-label img { width: 170px; height: 170px; object-fit: contain; }
.print-label small { margin-top: 8px; font-size: 18px; }
.label-actions { display: flex; flex-direction: column; gap: 12px; }
.label-actions .van-button { height: 76px; border-radius: 14px; font-size: 24px; }
.after-sales-mobile ::v-deep .van-nav-bar__title { font-size: 22px; font-weight: 600; }
.after-sales-mobile ::v-deep .van-nav-bar__arrow { font-size: 25px; }
.after-sales-mobile ::v-deep .van-tag { padding: 4px 9px; font-size: 18px; line-height: 26px; }
.after-sales-mobile ::v-deep .van-button { font-size: 21px; }
.after-sales-mobile ::v-deep .submit-intake { font-size: 26px; }
.after-sales-mobile ::v-deep .van-field__label { font-size: 21px; line-height: 1.5; }
.after-sales-mobile ::v-deep .van-field__control { font-size: 21px; line-height: 1.55; }
.after-sales-mobile ::v-deep .van-field__word-limit { font-size: 17px; }
.after-sales-mobile ::v-deep .van-checkbox__label { font-size: 20px; line-height: 1.55; }
.after-sales-mobile ::v-deep .van-notice-bar__content { font-size: 18px; }
.after-sales-mobile ::v-deep .van-action-sheet__item,
.after-sales-mobile ::v-deep .van-action-sheet__cancel { font-size: 21px; }
@media (max-width: 440px) {
    .hero-card { padding: 22px 18px; }
    .hero-icon { flex-basis: 72px; width: 72px; height: 72px; border-radius: 20px; font-size: 40px; }
    .hero-card h1 { font-size: 30px; }
    .hero-card p { font-size: 19px; }
    .no-code-panel { align-items: flex-start; flex-wrap: wrap; }
    .no-code-button { width: 100%; }
}
@media (min-width: 700px) { .mobile-content { padding-top: 28px; } .hero-card h1 { font-size: 38px; } }
</style>
